/**
 * Editor Landing Page V4 - Premium full-screen.
 * Rută: /op/editor-lp și /op/editor-lp/:id
 */
import { useState, useRef, useEffect, useMemo } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import api from "../utils/api";
import PremiumLayout from "../components/layout/PremiumLayout";
import LPPreview from "../components/LPPreview";
import "../styles/PremiumDashboard.css";

// ─── Constants ────────────────────────────────────────────────────────────────
const LAYOUTS = [
  { id: "classic", label: "Classic Hero", icon: "🎯", desc: "Titlu + buton centrat" },
  { id: "split", label: "Split Screen", icon: "◧", desc: "Stânga text, dreapta buton" },
  { id: "minimal", label: "Minimalist", icon: "✦", desc: "Clean, fără distrageri" },
  { id: "card", label: "Card Float", icon: "🪟", desc: "Card flotant pe fundal color" },
  { id: "ad", label: "Cu Reclamă", icon: "📣", desc: "Reclamă fullscreen → formular" },
];

const CULORI_PRESET = [
  "#2563EB","#1D4ED8","#3B82F6","#0EA5E9","#0891B2","#0D9488",
  "#16A34A","#15803D","#65A30D","#CA8A04","#D97706","#EA580C",
  "#DC2626","#B91C1C","#E11D48","#BE185D","#9333EA","#7C3AED",
  "#6D28D9","#4F46E5","#0F172A","#374151","#64748B","#94A3B8",
];

const TEXT_COLORS_GRID = [
  "#000000","#1E293B","#374151","#64748B","#94A3B8","#CBD5E1","#E2E8F0","#FFFFFF",
  "#DC2626","#EA580C","#CA8A04","#16A34A","#0891B2","#2563EB","#7C3AED","#BE185D",
  "#FCA5A5","#FED7AA","#FEF08A","#BBF7D0","#BAE6FD","#BFDBFE","#DDD6FE","#FBCFE8",
];

const BG_COLORS_GRID = [
  "transparent","#FFFFFF","#F8FAFC","#F1F5F9","#E2E8F0","#CBD5E1","#94A3B8","#0F172A",
  "#FEF2F2","#FFF7ED","#FEFCE8","#F0FDF4","#F0F9FF","#EFF6FF","#F5F3FF","#FFF1F2",
  "#FCA5A5","#FED7AA","#FEF08A","#BBF7D0","#BAE6FD","#BFDBFE","#DDD6FE","#FBCFE8",
];

const CAMPURI_SUGERATE = [
  "Județ","Oraș","Adresă","Data nașterii","Marca mașinii","Model","An fabricație",
  "Nr. înmatriculare","Suprafață (mp)","Mesaj","Cod poștal",
];

const DEMO_DATA = {
  "Nume complet": "Maria Ionescu", "Prenume": "Maria", "Telefon": "0745 123 456",
  "Email": "maria@email.com", "Județ": "București", "Oraș": "București",
  "Adresă": "Str. Florilor 12", "Data nașterii": "15/03/1985",
  "Marca mașinii": "Dacia", "Model": "Logan", "An fabricație": "2021",
  "Nr. înmatriculare": "B 123 ABC", "Suprafață (mp)": "75",
  "Mesaj": "Doresc o ofertă...", "Cod poștal": "010101",
};

function slugify(str) {
  if (!str || typeof str !== 'string') return 'pagina-' + Date.now();
  return str.trim().toLowerCase()
    .replace(/[ăâ]/g, 'a').replace(/[î]/g, 'i').replace(/[șş]/g, 's').replace(/[țţ]/g, 't')
    .replace(/\s+/g, '-').replace(/[^a-z0-9-]/g, '').replace(/-+/g, '-') || 'pagina-' + Date.now();
}

// ─── Color Picker Popup ───────────────────────────────────────────────────────
function ColorPickerPopup({ colors, onSelect, onClose }) {
  return (
    <div style={{
      position: "absolute", top: "calc(100% + 4px)", left: 0, zIndex: 300,
      background: "white", border: "1px solid #E2E8F0", borderRadius: 10,
      padding: 10, boxShadow: "0 8px 24px rgba(0,0,0,0.18)", width: 220
    }}>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(8, 1fr)", gap: 4 }}>
        {colors.map((c, i) => (
          <div key={i} onMouseDown={(e) => { e.preventDefault(); onSelect(c); onClose(); }} style={{
            width: 22, height: 22, borderRadius: 4, cursor: "pointer",
            background: c === "transparent" ? "white" : c,
            border: c === "transparent" ? "2px dashed #CBD5E1" : "1px solid rgba(0,0,0,0.1)",
          }} />
        ))}
      </div>
      <div style={{ marginTop: 8, borderTop: "1px solid #F1F5F9", paddingTop: 8 }}>
        <input type="color" onChange={(e) => { onSelect(e.target.value); }}
          style={{ width: "100%", height: 28, borderRadius: 4, border: "1px solid #CBD5E1", cursor: "pointer" }} />
        <div style={{ fontSize: 10, color: "#94A3B8", textAlign: "center", marginTop: 4 }}>Culoare personalizată</div>
      </div>
    </div>
  );
}

// ─── Rich Text Editor: componente toolbar (în afara RichTextEditor pentru a evita cursor jump) ─
function RteDivider() {
  return <div style={{ width: 1, height: 18, background: "#E2E8F0", margin: "0 2px" }} />;
}
function RteBtn({ onClick, title, children, style: s }) {
  return (
    <button type="button" title={title} onMouseDown={(e) => { e.preventDefault(); onClick(); }}
      style={{ padding: "3px 7px", border: "none", borderRadius: 4, cursor: "pointer", fontSize: 11, background: "transparent", color: "#374151", fontFamily: "inherit", ...s }}>
      {children}
    </button>
  );
}

// ─── Rich Text Editor: stiluri container (constante în afara componentei = fără re-render inutil) ─
const RTE_CONTAINER_STYLE = { border: "1px solid #CBD5E1", borderRadius: 8, overflow: "visible" };
const RTE_TOOLBAR_STYLE = { background: "#F8FAFC", borderBottom: "1px solid #E2E8F0", padding: "5px 6px", display: "flex", flexWrap: "wrap", gap: 1, alignItems: "center", borderRadius: "8px 8px 0 0" };
const RTE_EDITOR_STYLE = { minHeight: 80, padding: "10px 12px", fontSize: 13, color: "#1E293B", outline: "none", lineHeight: 1.7 };

// ─── Rich Text Editor ─────────────────────────────────────────────────────────
function RichTextEditor({ value, onChange }) {
  const editorRef = useRef(null);
  const [popup, setPopup] = useState(null);
  const lastSentRef = useRef(undefined);

  useEffect(() => {
    if (!editorRef.current) return;
    if (lastSentRef.current !== value) {
      lastSentRef.current = value;
      editorRef.current.innerHTML = value || "";
    }
  }, [value]);

  const exec = (cmd, val = null) => {
    editorRef.current?.focus();
    document.execCommand(cmd, false, val);
    const html = editorRef.current?.innerHTML || "";
    lastSentRef.current = html;
    onChange(html);
  };

  const handleInput = () => {
    const html = editorRef.current?.innerHTML || "";
    lastSentRef.current = html;
    onChange(html);
  };

  return (
    <div style={RTE_CONTAINER_STYLE}>
      <div style={RTE_TOOLBAR_STYLE}>
        <select onChange={(e) => exec("fontName", e.target.value)} style={{ fontSize: 10, padding: "2px 3px", borderRadius: 3, border: "1px solid #E2E8F0", width: 82, color: "#0F172A" }}>
          {["Arial","Georgia","Trebuchet MS","Verdana","Times New Roman","Tahoma"].map(f => <option key={f} value={f}>{f}</option>)}
        </select>
        <select onChange={(e) => exec("fontSize", e.target.value)} style={{ fontSize: 10, padding: "2px 3px", borderRadius: 3, border: "1px solid #E2E8F0", width: 48, color: "#0F172A" }}>
          {[["1","10px"],["2","13px"],["3","16px"],["4","18px"],["5","24px"],["6","32px"],["7","48px"]].map(([v]) => <option key={v} value={v}>{v}</option>)}
        </select>
        <RteDivider />
        <RteBtn title="Bold" onClick={() => exec("bold")} style={{ fontWeight: 700 }}>B</RteBtn>
        <RteBtn title="Italic" onClick={() => exec("italic")} style={{ fontStyle: "italic" }}>I</RteBtn>
        <RteBtn title="Underline" onClick={() => exec("underline")} style={{ textDecoration: "underline" }}>U</RteBtn>
        <RteBtn title="Strikethrough" onClick={() => exec("strikeThrough")} style={{ textDecoration: "line-through" }}>S</RteBtn>
        <RteDivider />
        <select onChange={(e) => exec("formatBlock", e.target.value)} style={{ fontSize: 10, padding: "2px 3px", borderRadius: 3, border: "1px solid #E2E8F0", width: 60, color: "#0F172A" }}>
          <option value="p">Normal</option>
          <option value="h1">Titlu 1</option>
          <option value="h2">Titlu 2</option>
          <option value="h3">Titlu 3</option>
          <option value="blockquote">Citat</option>
        </select>
        <RteDivider />
        <RteBtn title="Stânga" onClick={() => exec("justifyLeft")}>≡←</RteBtn>
        <RteBtn title="Centru" onClick={() => exec("justifyCenter")}>≡</RteBtn>
        <RteBtn title="Dreapta" onClick={() => exec("justifyRight")}>≡→</RteBtn>
        <RteBtn title="Justify" onClick={() => exec("justifyFull")}>≡≡</RteBtn>
        <RteDivider />
        <RteBtn title="Listă puncte" onClick={() => exec("insertUnorderedList")}>• Listă</RteBtn>
        <RteBtn title="Listă numerotată" onClick={() => exec("insertOrderedList")}>1. Listă</RteBtn>
        <RteBtn title="Indentare" onClick={() => exec("indent")}>→|</RteBtn>
        <RteBtn title="Dezindentare" onClick={() => exec("outdent")}>|←</RteBtn>
        <RteDivider />
        <div style={{ position: "relative" }}>
          <RteBtn title="Culoare text" onClick={() => setPopup(popup === "text" ? null : "text")}
            style={{ display: "flex", alignItems: "center", gap: 2 }}>
            <span style={{ fontWeight: 700 }}>A</span>
            <span style={{ fontSize: 8 }}>▼</span>
          </RteBtn>
          {popup === "text" && <ColorPickerPopup colors={TEXT_COLORS_GRID} onSelect={(c) => exec("foreColor", c)} onClose={() => setPopup(null)} />}
        </div>
        <div style={{ position: "relative" }}>
          <RteBtn title="Fundal text" onClick={() => setPopup(popup === "bg" ? null : "bg")}
            style={{ display: "flex", alignItems: "center", gap: 2 }}>
            <span style={{ background: "#FEF08A", padding: "0 2px", fontWeight: 700 }}>A</span>
            <span style={{ fontSize: 8 }}>▼</span>
          </RteBtn>
          {popup === "bg" && <ColorPickerPopup colors={BG_COLORS_GRID} onSelect={(c) => exec("hiliteColor", c)} onClose={() => setPopup(null)} />}
        </div>
        <RteDivider />
        <RteBtn title="Link" onClick={() => { const u = prompt("URL:"); if (u) exec("createLink", u); }}>Link</RteBtn>
        <RteBtn title="Elimină link" onClick={() => exec("unlink")}>-Link</RteBtn>
        <RteDivider />
        <RteBtn title="Șterge formatare" onClick={() => exec("removeFormat")} style={{ color: "#DC2626" }}>✕ fmt</RteBtn>
      </div>
      <div ref={editorRef} contentEditable suppressContentEditableWarning
        onInput={handleInput}
        style={RTE_EDITOR_STYLE} />
    </div>
  );
}

// ─── Toggle ───────────────────────────────────────────────────────────────────
function Toggle({ value, onChange, label, sublabel, culoare }) {
  return (
    <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "9px 0" }}>
      <div>
        <div style={{ fontSize: 12, fontWeight: 600, color: "#374151" }}>{label}</div>
        {sublabel && <div style={{ fontSize: 10, color: "#94A3B8", marginTop: 1 }}>{sublabel}</div>}
      </div>
      <div role="button" tabIndex={0} onClick={() => onChange(!value)} onKeyDown={(e) => e.key === "Enter" && onChange(!value)} style={{
        width: 42, height: 23, borderRadius: 12, background: value ? culoare : "#CBD5E1",
        cursor: "pointer", position: "relative", transition: "all 0.2s", flexShrink: 0, marginLeft: 12
      }}>
        <div style={{
          width: 19, height: 19, borderRadius: "50%", background: "white",
          position: "absolute", top: 2, left: value ? 21 : 2, transition: "all 0.2s",
          boxShadow: "0 1px 3px rgba(0,0,0,0.2)"
        }} />
      </div>
    </div>
  );
}

// ─── Main ─────────────────────────────────────────────────────────────────────
export default function LPEditorV4() {
  const navigate = useNavigate();
  const location = useLocation();
  const isAgent = location.pathname.startsWith("/agent/");
  const role = isAgent ? "agent" : "op";
  const match = location.pathname.match(/\/editor-lp\/(\d+)$/);
  const id = match ? match[1] : null;
  const isEdit = Boolean(id);
  const apiBase = role === "agent" ? "/agent/landing-pages" : "/op/landing-pages";
  const listPath = role === "agent" ? "/agent/landing-pages" : "/op/landing-pages";

  const [step, setStep] = useState(1);
  const [isMobile, setIsMobile] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [layout, setLayout] = useState("classic");
  const [titlu, setTitlu] = useState("");
  const [subtitlu, setSubtitlu] = useState("");
  const [textExtra, setTextExtra] = useState("");
  const [buton, setButon] = useState("Solicită ofertă gratuită");
  const [culoare, setCuloare] = useState("#2563EB");
  const [campuri, setCampuri] = useState([]);
  const [campNou, setCampNou] = useState("");
  const [tipCampNou, setTipCampNou] = useState("text");
  const [smsValidare, setSmsValidare] = useState(true);
  const [allowAmbassadors, setAllowAmbassadors] = useState(true);
  const [adType, setAdType] = useState("banner");
  const [adDuration, setAdDuration] = useState(15);
  const [adText, setAdText] = useState("");
  const [adSubtext, setAdSubtext] = useState("");
  const [adUrl, setAdUrl] = useState("");
  const [adVideoUrl, setAdVideoUrl] = useState("");
  const [adBgColor, setAdBgColor] = useState("#0F172A");
  const [gaEnabled, setGaEnabled] = useState(false);
  const [gaId, setGaId] = useState("");
  const [fbEnabled, setFbEnabled] = useState(false);
  const [fbPixelId, setFbPixelId] = useState("");
  const [localVideoUrl, setLocalVideoUrl] = useState("");
  const [videoFile, setVideoFile] = useState(null);
  const [videoDurationError, setVideoDurationError] = useState("");
  const [uploadingVideo, setUploadingVideo] = useState(false);
  const videoInputRef = useRef(null);

  useEffect(() => {
    if (isEdit && id) {
      const fetchLp = async () => {
        try {
          const res = await api.get(`${apiBase}/${id}`);
          const p = res.data?.page || res.data?.landingPage;
          if (p) {
            setTitlu(p.titlu || "");
            setSubtitlu(p.descriere?.replace(/<[^>]*>/g, "").split("\n")[0] || "");
            setTextExtra(p.descriere || "");
            setButon(p.textButonCTA || "Solicită ofertă gratuită");
            setCuloare(p.culoareButonCTA || "#2563EB");
            setSmsValidare(p.smsValidare !== false);
            setAllowAmbassadors(p.allowAmbassadors !== false);
            let cfg = {};
            try {
              if (p.designConfig) {
                cfg = typeof p.designConfig === "string" ? JSON.parse(p.designConfig) : p.designConfig;
                if (!cfg || typeof cfg !== "object") cfg = {};
              }
            } catch (_) { cfg = {}; }
            setLayout(cfg.layout || "classic");
            setAdType(cfg.adType || "banner");
            setAdDuration(Math.min(50, Math.max(3, Number(cfg.adDuration) || 15)));
            setAdText(cfg.adText || "");
            setAdSubtext(cfg.adSubtext || "");
            setAdUrl(cfg.adUrl || "");
            setAdVideoUrl(cfg.adVideoUrl != null ? String(cfg.adVideoUrl) : "");
            setAdBgColor(cfg.adBgColor || "#0F172A");
            const cfs = p.customFields ? (typeof p.customFields === "string" ? JSON.parse(p.customFields) : p.customFields) : [];
            const stdLabels = ["Nume complet","Prenume","Telefon","Email"];
            const extraOnly = (Array.isArray(cfs) ? cfs : []).filter(f => {
              const lb = (f.label || f || "").trim();
              return lb && !stdLabels.some(s => lb.toLowerCase().replace(/\s+/g,"") === s.toLowerCase().replace(/\s+/g,""));
            });
            setCampuri(extraOnly.map(f => ({ label: f.label || f, activ: true, type: f.type || "text", custom: !!f.custom })));
            setGaId(p.googleAnalyticsId || "");
            setGaEnabled(!!p.googleAnalyticsId);
            setFbPixelId(p.facebookPixelId || "");
            setFbEnabled(!!p.facebookPixelId);
            setLocalVideoUrl(p.localVideoUrl || "");
          }
        } catch (err) {
          console.error("Eroare încărcare LP:", err);
          navigate(listPath);
        }
      };
      fetchLp();
    }
  }, [id, isEdit, navigate, apiBase, listPath]);

  const handleVideoFileSelect = (e) => {
    const file = e.target.files?.[0];
    setVideoDurationError("");
    setVideoFile(null);
    if (!file) return;
    if (!['video/mp4', 'video/webm'].includes(file.type)) {
      setVideoDurationError("Doar MP4 sau WebM.");
      e.target.value = "";
      return;
    }
    const url = URL.createObjectURL(file);
    const video = document.createElement("video");
    video.preload = "metadata";
    video.onloadedmetadata = () => {
      URL.revokeObjectURL(url);
      if (video.duration > 61) {
        setVideoDurationError("Maxim 1 minut!");
        if (videoInputRef.current) videoInputRef.current.value = "";
        return;
      }
      setVideoFile(file);
      setVideoDurationError("");
    };
    video.onerror = () => {
      URL.revokeObjectURL(url);
      setVideoDurationError("Nu s-a putut citi video.");
      if (videoInputRef.current) videoInputRef.current.value = "";
    };
    video.src = url;
  };

  const addCamp = (label, opts = {}) => {
    const type = opts.type || "text";
    const custom = opts.custom || false;
    if (!String(label).trim() || campuri.find(c => c.label === label)) return;
    setCampuri(prev => [...prev, { label: String(label).trim(), activ: true, type, custom }]);
    setCampNou("");
  };
  const removeCamp = (i) => setCampuri(prev => prev.filter((_, idx) => idx !== i));

  const previewVideoObjectUrl = useMemo(() => (videoFile ? URL.createObjectURL(videoFile) : null), [videoFile]);
  useEffect(() => {
    return () => {
      if (previewVideoObjectUrl) URL.revokeObjectURL(previewVideoObjectUrl);
    };
  }, [previewVideoObjectUrl]);

  // Derivat direct din stare la fiecare render — fără useMemo — pentru reactivitate în timp real la tastare
  const config = {
    titlu,
    subtitlu,
    textExtra,
    buton,
    culoare,
    campuri,
    layout,
    smsValidare,
    allowAmbassadors,
    adType,
    adDuration,
    adText,
    adSubtext,
    adUrl,
    adBgColor,
    adVideoUrl,
    localVideoUrl,
    previewVideoUrl: previewVideoObjectUrl || null,
  };

  const handleSave = async (draftStatus) => {
    const isDraft = draftStatus === true;
    setError("");
    if (!titlu.trim()) {
      setError("Titlul este obligatoriu.");
      return;
    }
    if (videoDurationError) return;
    // Validare video: eroare DOAR dacă layout e "ad", tip video, ȘI lipsesc atât URL/link cât și fișierul local
    const hasVideoUrl = !!(localVideoUrl && String(localVideoUrl).trim()) || !!(adVideoUrl && String(adVideoUrl).trim());
    const hasLocalFile = !!videoFile;
    if (layout === "ad" && adType === "video" && !hasVideoUrl && !hasLocalFile) {
      setError("Lipsește fișierul video. Încarcă un video sau adaugă un link.");
      return;
    }
    setLoading(true);
    try {
      let finalLocalVideoUrl = localVideoUrl || "";
      if (videoFile) {
        setUploadingVideo(true);
        const formData = new FormData();
        formData.append("video", videoFile);
        const uploadRes = await api.post("/upload/video", formData);
        setUploadingVideo(false);
        finalLocalVideoUrl = uploadRes.data?.url || "";
        setLocalVideoUrl(finalLocalVideoUrl);
        setVideoFile(null);
        if (videoInputRef.current) videoInputRef.current.value = "";
      }
      const payload = {
        titlu: titlu.trim(),
        slug: slugify(titlu),
        descriere: textExtra || (subtitlu ? `<p>${subtitlu}</p>` : ""),
        textButonCTA: buton || "Solicită ofertă gratuită",
        titluFormular: "Completează datele tale",
        culoareButonCTA: culoare,
        culoareFundal: "#FFFFFF",
        culoareTextTitlu: "#212121",
        culoareTextDescriere: "#64748B",
        smsValidare,
        allowAmbassadors,
        isDraft,
        activ: !isDraft,
        campuriFormular: JSON.stringify(["nume", "telefon", "email", ...campuri.filter(c => c.activ).map(c => c.label.toLowerCase().replace(/\s+/g, "_"))]),
        customFields: JSON.stringify(campuri.filter(c => c.activ).map(c => ({ label: c.label, type: c.type || "text", required: false }))),
        designConfig: JSON.stringify({
          layout,
          formPosition: "right",
          adType,
          adDuration,
          adText,
          adSubtext,
          adUrl,
          adVideoUrl: adVideoUrl != null ? String(adVideoUrl).trim() : "",
          adBgColor,
        }),
        googleAnalyticsId: gaEnabled ? gaId : "",
        facebookPixelId: fbEnabled ? fbPixelId : "",
        localVideoUrl: finalLocalVideoUrl || undefined,
      };
      if (isEdit) {
        await api.put(`${apiBase}/${id}`, payload);
      } else {
        await api.post(apiBase, payload);
      }
      navigate(listPath);
    } catch (err) {
      setError(err.response?.data?.error || "Eroare la salvare.");
    } finally {
      setLoading(false);
      setUploadingVideo(false);
    }
  };

  const steps = [
    { n: 1, label: "Layout" },
    { n: 2, label: "Conținut" },
    { n: 3, label: "Design & Tracking" },
  ];

  const topActions = (
    <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
      {error && <span style={{ fontSize: 12, color: "#DC2626" }}>{error}</span>}
      <button type="button" onClick={() => navigate(listPath)} style={{
        padding: "8px 16px", borderRadius: 8, border: "1px solid #CBD5E1",
        background: "white", fontSize: 13, fontWeight: 600, color: "#374151", cursor: "pointer"
      }}>Anulează</button>
      <button type="button" onClick={() => handleSave(true)} disabled={loading} style={{
        padding: "8px 16px", borderRadius: 8, border: `2px solid ${culoare}`,
        background: "white", fontSize: 13, fontWeight: 600, color: culoare, cursor: loading ? "wait" : "pointer",
        opacity: loading ? 0.7 : 1
      }}>{loading ? (uploadingVideo ? "Se încarcă video..." : "Se salvează...") : "📝 Salvează ca Ciornă"}</button>
      <button type="button" onClick={() => handleSave(false)} disabled={loading} style={{
        padding: "8px 20px", borderRadius: 8, border: "none",
        background: culoare, color: "white", fontSize: 13, fontWeight: 700, cursor: loading ? "wait" : "pointer",
        opacity: loading ? 0.8 : 1
      }}>{loading ? (uploadingVideo ? "Se încarcă video..." : "Se salvează...") : "🚀 Publică Oferta"}</button>
    </div>
  );

  return (
    <PremiumLayout title="Editor Landing Page" subtitle="Creează sau editează pagina de ofertă" variant="op" hideSidebar topActions={topActions} contentClassName="p-0 overflow-hidden">
      <div style={{ fontFamily: "'Segoe UI', sans-serif", display: "flex", height: "calc(100vh - 80px)", background: "#F1F5F9", overflow: "hidden" }}>

        {/* LEFT PANEL */}
        <div style={{ width: 460, background: "white", display: "flex", flexDirection: "column", boxShadow: "2px 0 12px rgba(0,0,0,0.08)", zIndex: 10 }}>

          <div style={{ padding: "14px 18px", borderBottom: "1px solid #E2E8F0" }}>
            <div style={{ fontSize: 14, fontWeight: 700, color: "#1E293B", marginBottom: 10 }}>✦ Editor Landing Page</div>
            <div style={{ display: "flex", gap: 3 }}>
              {steps.map((s, i) => (
                <div key={s.n} style={{ display: "flex", alignItems: "center", gap: 3, flex: 1 }}>
                  <div role="button" tabIndex={0} onClick={() => step >= s.n && setStep(s.n)} onKeyDown={(e) => step >= s.n && e.key === "Enter" && setStep(s.n)} style={{
                    display: "flex", alignItems: "center", gap: 5, cursor: step >= s.n ? "pointer" : "default",
                    padding: "5px 6px", borderRadius: 6, flex: 1, justifyContent: "center",
                    background: step === s.n ? culoare : step > s.n ? `${culoare}20` : "#F1F5F9",
                  }}>
                    <span style={{ fontSize: 10, fontWeight: 700, color: step === s.n ? "white" : step > s.n ? culoare : "#94A3B8" }}>
                      {step > s.n ? "✓" : s.n}
                    </span>
                    <span style={{ fontSize: 10, fontWeight: 600, color: step === s.n ? "white" : step > s.n ? culoare : "#94A3B8", whiteSpace: "nowrap" }}>
                      {s.label}
                    </span>
                  </div>
                  {i < steps.length - 1 && <div style={{ width: 4, height: 1, background: "#E2E8F0" }} />}
                </div>
              ))}
            </div>
          </div>

          <div style={{ flex: 1, overflowY: "auto", padding: "16px 18px" }}>

            {step === 1 && (
              <div>
                <div style={{ fontSize: 13, fontWeight: 700, color: "#1E293B", marginBottom: 4 }}>Alege layout-ul paginii</div>
                <div style={{ fontSize: 11, color: "#64748B", marginBottom: 14 }}>Layout-ul "Cu Reclamă" rulează un spot fullscreen înainte să apară formularul.</div>
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8, marginBottom: 20 }}>
                  {LAYOUTS.map(l => (
                    <div key={l.id} role="button" tabIndex={0} onClick={() => setLayout(l.id)} onKeyDown={(e) => e.key === "Enter" && setLayout(l.id)} style={{
                      padding: "14px 10px", borderRadius: 10,
                      border: `2px solid ${layout === l.id ? culoare : "#E2E8F0"}`,
                      background: layout === l.id ? `${culoare}10` : "white",
                      cursor: "pointer", textAlign: "center", transition: "all 0.15s",
                      ...(l.id === "ad" ? { gridColumn: "span 2", display: "flex", gap: 12, alignItems: "center", textAlign: "left", padding: "14px 16px" } : {})
                    }}>
                      <div style={{ fontSize: l.id === "ad" ? 28 : 22, marginBottom: l.id === "ad" ? 0 : 6 }}>{l.icon}</div>
                      <div>
                        <div style={{ fontSize: 12, fontWeight: 700, color: layout === l.id ? culoare : "#374151" }}>{l.label}</div>
                        <div style={{ fontSize: 10, color: "#94A3B8", marginTop: 2 }}>{l.desc}</div>
                      </div>
                    </div>
                  ))}
                </div>

                {layout === "ad" && (
                  <div style={{ padding: "14px", background: "#F8FAFC", borderRadius: 10, border: "1px solid #E2E8F0" }}>
                    <div style={{ fontSize: 12, fontWeight: 700, color: "#1E293B", marginBottom: 12 }}>Configurare reclamă</div>
                    <div style={{ fontSize: 11, fontWeight: 600, color: "#374151", marginBottom: 6 }}>Tip conținut</div>
                    <div style={{ display: "flex", gap: 6, marginBottom: 12 }}>
                      {[{ id: "banner", label: "Imagine / Text" }, { id: "video", label: "Video" }].map(t => (
                        <div key={t.id} role="button" tabIndex={0} onClick={() => setAdType(t.id)} onKeyDown={(e) => e.key === "Enter" && setAdType(t.id)} style={{
                          flex: 1, padding: "8px", borderRadius: 7, border: `2px solid ${adType === t.id ? culoare : "#E2E8F0"}`,
                          background: adType === t.id ? `${culoare}10` : "white", cursor: "pointer",
                          fontSize: 11, fontWeight: adType === t.id ? 700 : 500,
                          color: adType === t.id ? culoare : "#374151", textAlign: "center"
                        }}>{t.label}</div>
                      ))}
                    </div>
                    <div style={{ fontSize: 11, fontWeight: 600, color: "#374151", marginBottom: 4 }}>Durată: <span style={{ color: culoare }}>{adDuration}s</span> (max 50s)</div>
                    <input type="range" min={3} max={50} value={adDuration} onChange={e => setAdDuration(Number(e.target.value))} style={{ width: "100%", marginBottom: 12 }} />
                    {adType === "video" && (
                      <>
                        <div style={{ fontSize: 11, fontWeight: 600, color: "#374151", marginBottom: 4 }}>URL Video (YouTube)</div>
                        <input value={adVideoUrl} onChange={e => setAdVideoUrl(e.target.value)} style={{ width: "100%", padding: "8px 10px", borderRadius: 7, border: "1px solid #CBD5E1", fontSize: 13, color: "#0F172A", boxSizing: "border-box", marginBottom: 12 }} placeholder="Ex: https://youtube.com/shorts/..." />
                      </>
                    )}
                    {adType === "banner" && (
                      <>
                        <div style={{ fontSize: 11, fontWeight: 600, color: "#374151", marginBottom: 4 }}>URL destinație (opțional)</div>
                        <input value={adUrl} onChange={e => setAdUrl(e.target.value)} style={{ width: "100%", padding: "8px 10px", borderRadius: 7, border: "1px solid #CBD5E1", fontSize: 12, boxSizing: "border-box", marginBottom: 8, color: "#0F172A" }} placeholder="https://..." />
                      </>
                    )}
                    <div style={{ fontSize: 11, fontWeight: 600, color: "#374151", marginBottom: 4 }}>Titlu reclamă</div>
                    <input value={adText} onChange={e => setAdText(e.target.value)} style={{ width: "100%", padding: "8px 10px", borderRadius: 7, border: "1px solid #CBD5E1", fontSize: 12, boxSizing: "border-box", marginBottom: 8, color: "#0F172A" }} placeholder="Ex: Ofertă specială CASCO!" />
                    <div style={{ fontSize: 11, fontWeight: 600, color: "#374151", marginBottom: 4 }}>Subtitlu reclamă</div>
                    <input value={adSubtext} onChange={e => setAdSubtext(e.target.value)} style={{ width: "100%", padding: "8px 10px", borderRadius: 7, border: "1px solid #CBD5E1", fontSize: 12, boxSizing: "border-box", marginBottom: 8, color: "#0F172A" }} placeholder="Ex: De la 299 RON/an" />
                    <div style={{ fontSize: 11, fontWeight: 600, color: "#374151", marginBottom: 6 }}>Culoare fundal reclamă</div>
                    <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
                      <input type="color" value={adBgColor} onChange={e => setAdBgColor(e.target.value)} style={{ width: 38, height: 32, borderRadius: 6, border: "1px solid #CBD5E1", cursor: "pointer", padding: 2 }} />
                      <span style={{ fontSize: 11, color: "#64748B" }}>Fundal întunecat recomandat</span>
                    </div>
                  </div>
                )}
              </div>
            )}

            {step === 2 && (
              <div key="step2-conținut">
                <div style={{ fontSize: 13, fontWeight: 700, color: "#1E293B", marginBottom: 14 }}>Conținut pagină</div>
                <label style={{ fontSize: 11, fontWeight: 600, color: "#374151", display: "block", marginBottom: 4 }}>Titlu principal *</label>
                <input key="lp-titlu" type="text" value={titlu} onChange={e => setTitlu(e.target.value)} style={{ width: "100%", padding: "9px 11px", borderRadius: 7, border: "1px solid #CBD5E1", fontSize: 13, marginBottom: 12, boxSizing: "border-box", color: "#0F172A" }} placeholder="Ex: Obține oferta ta în 2 minute" />
                <label style={{ fontSize: 11, fontWeight: 600, color: "#374151", display: "block", marginBottom: 4 }}>Subtitlu</label>
                <input key="lp-subtitlu" type="text" value={subtitlu} onChange={e => setSubtitlu(e.target.value)} style={{ width: "100%", padding: "9px 11px", borderRadius: 7, border: "1px solid #CBD5E1", fontSize: 13, marginBottom: 12, boxSizing: "border-box", color: "#0F172A" }} placeholder="Completează și te contactăm rapid" />
                <div style={{ marginBottom: 16 }}>
                  <div style={{ fontSize: 11, fontWeight: 600, color: "#374151", marginBottom: 4 }}>Video pagină (opțional, max 1 min)</div>
                  <input ref={videoInputRef} type="file" accept="video/mp4,video/webm" onChange={handleVideoFileSelect} style={{ width: "100%", padding: "8px", borderRadius: 7, border: "1px solid #CBD5E1", fontSize: 12, boxSizing: "border-box" }} />
                  {videoDurationError && <div style={{ fontSize: 11, color: "#DC2626", marginTop: 4 }}>{videoDurationError}</div>}
                  {(localVideoUrl || videoFile) && <div style={{ fontSize: 11, color: "#16A34A", marginTop: 4 }}>{videoFile ? "Fișier selectat (OK)" : "Video salvat"}</div>}
                </div>
                <label style={{ fontSize: 11, fontWeight: 600, color: "#374151", display: "block", marginBottom: 6 }}>Text suplimentar</label>
                <div key="lp-richtext" style={{ marginBottom: 16 }}><RichTextEditor value={textExtra} onChange={setTextExtra} /></div>
                <label style={{ fontSize: 11, fontWeight: 600, color: "#374151", display: "block", marginBottom: 4 }}>Text buton</label>
                <input key="lp-buton" type="text" value={buton} onChange={e => setButon(e.target.value)} style={{ width: "100%", padding: "9px 11px", borderRadius: 7, border: "1px solid #CBD5E1", fontSize: 13, marginBottom: 20, boxSizing: "border-box", color: "#0F172A" }} />
                <div style={{ padding: "12px", background: "#F8FAFC", borderRadius: 8, marginBottom: 20, border: "1px solid #E2E8F0" }}>
                  <div style={{ fontSize: 11, fontWeight: 700, color: "#374151", marginBottom: 8 }}>🔒 Câmpuri Standard (Incluse automat)</div>
                  <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                    {["Nume complet", "Email", "Telefon"].map(c => (
                      <span key={c} style={{ fontSize: 11, background: "white", padding: "4px 10px", borderRadius: 4, border: "1px solid #CBD5E1", color: "#64748B", fontWeight: 600 }}>{c}</span>
                    ))}
                  </div>
                </div>
                <div style={{ fontSize: 11, fontWeight: 700, color: "#374151", marginBottom: 8 }}>Câmpuri formular (opționale)</div>
                <div style={{ display: "flex", flexWrap: "wrap", gap: 5, marginBottom: 10 }}>
                  {CAMPURI_SUGERATE.filter(c => !campuri.find(x => x.label === c)).slice(0, 9).map(c => (
                    <div key={c} role="button" tabIndex={0} onClick={() => addCamp(c)} onKeyDown={(e) => e.key === "Enter" && addCamp(c)} style={{ padding: "4px 8px", borderRadius: 20, background: "#F1F5F9", border: "1px solid #E2E8F0", fontSize: 11, color: "#374151", cursor: "pointer" }}>+ {c}</div>
                  ))}
                </div>
                <div style={{ display: "flex", gap: 6, marginBottom: 12, flexWrap: "wrap", alignItems: "center" }}>
                  <input value={campNou} onChange={e => setCampNou(e.target.value)} onKeyDown={e => e.key === "Enter" && addCamp(campNou, { custom: true, type: tipCampNou })}
                    style={{ flex: "1 1 120px", minWidth: 100, padding: "8px 10px", borderRadius: 7, border: "1px solid #CBD5E1", fontSize: 12, boxSizing: "border-box", color: "#0F172A" }} placeholder="Câmp personalizat..." />
                  <select value={tipCampNou} onChange={e => setTipCampNou(e.target.value)} style={{ padding: "8px 10px", borderRadius: 7, border: "1px solid #CBD5E1", fontSize: 12, color: "#0F172A", background: "white", minWidth: 120 }}>
                    <option value="text">Text scurt</option>
                    <option value="datetime-local">Dată și Oră</option>
                  </select>
                  <button type="button" onClick={() => addCamp(campNou, { custom: true, type: tipCampNou })} style={{ padding: "8px 12px", borderRadius: 7, border: "none", background: culoare, color: "white", fontSize: 12, fontWeight: 600, cursor: "pointer" }}>+ Adaugă</button>
                </div>
                <div style={{ display: "flex", flexDirection: "column", gap: 5 }}>
                  {campuri.map((c, i) => (
                    <div key={c.label + (c.custom ? "-custom" : "") + i} style={{ display: "flex", alignItems: "center", gap: 8, padding: "8px 10px", borderRadius: 7, border: `1px solid ${c.activ ? culoare : "#E2E8F0"}`, background: c.activ ? `${culoare}08` : "#F8FAFC" }}>
                      <div role="button" tabIndex={0} onClick={() => removeCamp(i)} onKeyDown={(e) => e.key === "Enter" && removeCamp(i)} style={{ width: 16, height: 16, borderRadius: 4, border: `2px solid ${c.activ ? culoare : "#CBD5E1"}`, background: c.activ ? culoare : "white", display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer", flexShrink: 0 }}>{c.activ && <span style={{ color: "white", fontSize: 9 }}>✓</span>}</div>
                      <span role="button" tabIndex={0} onClick={() => removeCamp(i)} onKeyDown={(e) => e.key === "Enter" && removeCamp(i)} style={{ flex: 1, fontSize: 12, color: c.activ ? "#1E293B" : "#94A3B8", cursor: "pointer" }}>{c.label}{c.type === "datetime-local" ? " (dată/oră)" : ""}</span>
                      <span role="button" tabIndex={0} onClick={() => removeCamp(i)} onKeyDown={(e) => e.key === "Enter" && removeCamp(i)} style={{ fontSize: 14, color: "#94A3B8", cursor: "pointer", padding: "0 4px" }}>×</span>
                    </div>
                  ))}
                  {campuri.length === 0 && <div style={{ textAlign: "center", fontSize: 12, color: "#94A3B8", padding: 16, background: "#F8FAFC", borderRadius: 7 }}>Adaugă câmpuri din lista de mai sus</div>}
                </div>
                <div style={{ borderTop: "1px solid #E2E8F0", marginTop: 14, paddingTop: 10 }}>
                  <Toggle value={smsValidare} onChange={setSmsValidare} label="Validare telefon prin SMS" sublabel="Recomandat pentru calitatea lead-urilor" culoare={culoare} />
                  <div style={{ marginTop: 12 }}>
                    <Toggle value={allowAmbassadors} onChange={setAllowAmbassadors} label="Activează Programul de Ambasadori" sublabel="Permite clienților care completează formularul să își genereze propriul link de recomandare." culoare={culoare} />
                  </div>
                </div>
              </div>
            )}

            {step === 3 && (
              <div>
                <div style={{ fontSize: 13, fontWeight: 700, color: "#1E293B", marginBottom: 14 }}>Design</div>
                <div style={{ fontSize: 11, fontWeight: 600, color: "#374151", marginBottom: 8 }}>Culoare principală</div>
                <div style={{ display: "grid", gridTemplateColumns: "repeat(8, 1fr)", gap: 6, marginBottom: 14 }}>
                  {CULORI_PRESET.map(c => (
                    <div key={c} role="button" tabIndex={0} onClick={() => setCuloare(c)} onKeyDown={(e) => e.key === "Enter" && setCuloare(c)} style={{ width: 32, height: 32, borderRadius: 6, background: c, cursor: "pointer", border: culoare === c ? "3px solid #0F172A" : "2px solid transparent", boxShadow: culoare === c ? "0 0 0 1px white inset" : "none", transition: "all 0.1s" }} />
                  ))}
                </div>
                <div style={{ display: "flex", gap: 8, alignItems: "center", marginBottom: 24 }}>
                  <input type="color" value={culoare} onChange={e => setCuloare(e.target.value)} style={{ width: 44, height: 38, borderRadius: 7, border: "1px solid #CBD5E1", cursor: "pointer", padding: 2 }} />
                  <input value={culoare} onChange={e => { if (/^#[0-9A-Fa-f]{6}$/.test(e.target.value)) setCuloare(e.target.value); }} style={{ flex: 1, padding: "9px 11px", borderRadius: 7, border: "1px solid #CBD5E1", fontSize: 13, color: "#0F172A" }} />
                </div>
                <div style={{ borderTop: "1px solid #E2E8F0", paddingTop: 16 }}>
                  <div style={{ fontSize: 13, fontWeight: 700, color: "#1E293B", marginBottom: 14 }}>Tracking</div>
                  <div style={{ marginBottom: 14, paddingBottom: 14, borderBottom: "1px solid #F1F5F9" }}>
                    <div style={{ fontSize: 12, fontWeight: 700, color: "#1E293B", marginBottom: 1 }}>Google Analytics 4</div>
                    <div style={{ fontSize: 10, color: "#94A3B8", marginBottom: 6 }}>Urmărire vizitatori și conversii</div>
                    <Toggle value={gaEnabled} onChange={setGaEnabled} label="Activează GA4" culoare={culoare} />
                    {gaEnabled && <input value={gaId} onChange={e => setGaId(e.target.value)} style={{ width: "100%", padding: "8px 10px", borderRadius: 7, border: "1px solid #CBD5E1", fontSize: 12, boxSizing: "border-box", marginTop: 6, color: "#0F172A" }} placeholder="G-XXXXXXXXXX" />}
                  </div>
                  <div>
                    <div style={{ fontSize: 12, fontWeight: 700, color: "#1E293B", marginBottom: 1 }}>Facebook Pixel</div>
                    <div style={{ fontSize: 10, color: "#94A3B8", marginBottom: 6 }}>Retargeting Meta Ads</div>
                    <Toggle value={fbEnabled} onChange={setFbEnabled} label="Activează FB Pixel" culoare={culoare} />
                    {fbEnabled && <input value={fbPixelId} onChange={e => setFbPixelId(e.target.value)} style={{ width: "100%", padding: "8px 10px", borderRadius: 7, border: "1px solid #CBD5E1", fontSize: 12, boxSizing: "border-box", marginTop: 6, color: "#0F172A" }} placeholder="123456789012345" />}
                  </div>
                </div>
              </div>
            )}
          </div>

          <div style={{ padding: "12px 18px", borderTop: "1px solid #E2E8F0", display: "flex", gap: 8 }}>
            {step > 1 && <button type="button" onClick={() => setStep(s => s - 1)} style={{ flex: 1, padding: "10px", borderRadius: 8, border: "1px solid #CBD5E1", background: "white", fontSize: 13, fontWeight: 600, color: "#374151", cursor: "pointer" }}>Înapoi</button>}
            {step < 3 ? <button type="button" onClick={() => setStep(s => s + 1)} style={{ flex: 2, padding: "10px", borderRadius: 8, border: "none", background: culoare, color: "white", fontSize: 13, fontWeight: 600, cursor: "pointer" }}>Continuă →</button> : null}
          </div>
        </div>

        {/* RIGHT — Preview */}
        <div style={{ flex: 1, display: "flex", flexDirection: "column", overflow: "hidden" }}>
          <div style={{ padding: "12px 20px", background: "white", borderBottom: "1px solid #E2E8F0", display: "flex", alignItems: "center", gap: 10 }}>
            <span style={{ fontSize: 13, fontWeight: 700, color: "#1E293B" }}>Preview live</span>
            <div style={{ fontSize: 10, padding: "2px 8px", borderRadius: 20, background: "#DCFCE7", color: "#16A34A", fontWeight: 700 }}>LIVE</div>
            {layout === "ad" && <div style={{ fontSize: 10, padding: "2px 10px", borderRadius: 20, background: "#FEF3C7", color: "#B45309", fontWeight: 700 }}>📣 Mod reclamă activ</div>}
            <div style={{ marginLeft: "auto", display: "flex", background: "#F1F5F9", borderRadius: 8, padding: 3, gap: 2 }}>
              <button type="button" onClick={() => setIsMobile(false)} style={{ padding: "5px 12px", borderRadius: 6, border: "none", fontSize: 12, fontWeight: 600, background: !isMobile ? "white" : "transparent", color: !isMobile ? "#1E293B" : "#94A3B8", cursor: "pointer", boxShadow: !isMobile ? "0 1px 3px rgba(0,0,0,0.08)" : "none" }}>🖥 Desktop</button>
              <button type="button" onClick={() => setIsMobile(true)} style={{ padding: "5px 12px", borderRadius: 6, border: "none", fontSize: 12, fontWeight: 600, background: isMobile ? "white" : "transparent", color: isMobile ? "#1E293B" : "#94A3B8", cursor: "pointer", boxShadow: isMobile ? "0 1px 3px rgba(0,0,0,0.08)" : "none" }}>📱 Mobil</button>
            </div>
          </div>
          <div style={{ flex: 1, overflowY: "auto", padding: "28px 20px", display: "flex", flexDirection: "column", alignItems: "center", gap: 16, pointerEvents: "auto" }}>
            <LPPreview config={config} isMobile={isMobile} step={step} />
            <div style={{ fontSize: 11, color: "#94A3B8", textAlign: "center", maxWidth: 400 }}>💡 Preview interactiv — apasă butonul pentru a vedea fluxul GDPR → formular</div>
          </div>
        </div>
      </div>
    </PremiumLayout>
  );
}
